/**
 * Referências externas em fluxos de chatbot (export/import entre workspaces).
 */

export const REF_KIND_AUTOMATION = "automationRule";
export const REF_KIND_USER = "user";
export const REF_KIND_AGENT = "agent";
export const REF_KIND_CHANNEL = "channel";
export const REF_KIND_TYPE = "type";
export const REF_KIND_SUBTYPE = "subtype";
export const REF_KIND_PAYMENT = "paymentMethod";
export const REF_KIND_DELIVERY = "deliveryMethod";
export const REF_KIND_POSITION = "position";

export const ENTITY_OBJECT_MAP = {
  origensdeleads: REF_KIND_CHANNEL,
  usuarios: REF_KIND_USER,
  agentesdeia: REF_KIND_AGENT,
  agentes: REF_KIND_AGENT,
  formasdepagamento: REF_KIND_PAYMENT,
  tiposdeleads: REF_KIND_TYPE,
  subtiposdeleads: REF_KIND_SUBTYPE,
  metodosdeentrega: REF_KIND_DELIVERY,
  cargos: REF_KIND_POSITION,
  positions: REF_KIND_POSITION,
};

const STATIC_ENTITY_OBJECTS = new Set(["statuslead"]);

function normalizeIdList(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v !== "");
  return [String(value)];
}

function formFieldEntries(parameterForm) {
  if (!parameterForm || typeof parameterForm !== "object") return [];
  const root = parameterForm.acoes || parameterForm.fields || parameterForm.condicoes || parameterForm;
  if (!root || typeof root !== "object") return [];
  return Object.entries(root);
}

export function buildIdentPath(slotId, slotById) {
  const parts = [];
  let current = slotById.get(Number(slotId));
  const seen = new Set();
  while (current) {
    const nid = Number(current.id);
    if (seen.has(nid)) break;
    seen.add(nid);
    parts.unshift(String(current.identificador || current.nome || "Nó").trim() || "Nó");
    const parentId = Number(current.id_pai) || 0;
    current = parentId > 0 ? slotById.get(parentId) : null;
  }
  return parts;
}

export function navPathKey(navPath) {
  if (!Array.isArray(navPath) || !navPath.length) return "";
  return navPath.map((s) => String(s).trim()).filter(Boolean).join(" > ");
}

function addRef(bucket, kind, id, meta = {}) {
  const key = String(id);
  if (!key || key === "0") return;
  if (!bucket[kind]) bucket[kind] = {};
  if (!bucket[kind][key]) {
    bucket[kind][key] = { id, kind, ...meta };
  } else {
    bucket[kind][key] = { ...bucket[kind][key], ...meta };
  }
}

function scanActionRefs(action, ctx, refs) {
  const tipo = Number(action?.tipo_acao) || 0;
  const idAcao = Number(action?.id_acao) || 0;

  if (tipo === 2 && idAcao) {
    const navPath = action.navPath || buildIdentPath(idAcao, ctx.slotById);
    if (navPath?.length) {
      action.navPath = navPath;
      addRef(refs, "navigation", navPathKey(navPath), {
        navPath,
        label: action.labels?.destino || navPathKey(navPath),
      });
    }
    return;
  }

  if (tipo === 3 && idAcao) {
    addRef(refs, REF_KIND_AUTOMATION, idAcao, {
      label: action.labels?.regra || action.labels?.nome || `Regra #${idAcao}`,
    });
    return;
  }

  if (tipo === 4 && idAcao === 4) {
    scanParametricValues(action.values, action.labels, ctx.parameterForm, refs);
  }
}

function scanParametricValues(values, labels, parameterForm, refs) {
  if (!values || typeof values !== "object") return;
  for (const [fieldName, fieldDef] of formFieldEntries(parameterForm)) {
    const objectType = fieldDef?.object;
    if (!objectType || STATIC_ENTITY_OBJECTS.has(objectType)) continue;
    const kind = ENTITY_OBJECT_MAP[objectType];
    if (!kind) continue;
    const raw = values[fieldName];
    for (const id of normalizeIdList(raw)) {
      if (kind === REF_KIND_AGENT) {
        const agentId = String(id || "").trim();
        if (!agentId) continue;
        const label =
          labels?.[fieldName] ||
          (Array.isArray(labels?.[fieldName]) ? labels[fieldName].join(", ") : null) ||
          fieldDef?.label ||
          agentId;
        addRef(refs, kind, agentId, { label: String(label), field: fieldName });
        continue;
      }

      const numId = Number(id);
      if (!numId) continue;
      const label =
        labels?.[fieldName] ||
        (Array.isArray(labels?.[fieldName]) ? labels[fieldName].join(", ") : null) ||
        fieldDef?.label ||
        `#${id}`;
      addRef(refs, kind, numId, { label: String(label), field: fieldName });
    }
  }
}

function walkFlowNodes(nodes, ctx, refs, visitor) {
  for (const node of nodes || []) {
    visitor(node, ctx, refs);
    if (node.exception) visitor(node.exception, ctx, refs);
    walkFlowNodes(node.children, ctx, refs, visitor);
  }
}

function visitNodeForRefs(node, ctx, refs) {
  const legacyTipo = Number(node.tipo_acao) || 0;
  const legacyId = Number(node.id_acao) || 0;
  if (legacyTipo === 3 && legacyId) {
    addRef(refs, REF_KIND_AUTOMATION, legacyId, { label: `Regra #${legacyId}` });
  }
  if (legacyTipo === 4 && legacyId === 4) {
    scanParametricValues(node.acao_parametros?.values, node.acao_parametros?.labels, ctx.parameterForm, refs);
  }

  const acoes = Array.isArray(node.acoes) ? node.acoes : [];
  for (const action of acoes) scanActionRefs(action, ctx, refs);

  const faqCfg = node.agente_ia || {};
  if (node.resposta_modo === "agente_ia" && faqCfg.agent_id) {
    addRef(refs, REF_KIND_AGENT, faqCfg.agent_id, {
      label: faqCfg.agent_name || faqCfg.agent_id,
    });
  }
}

/** Coleta referências externas a partir de slots (API) ou árvore do documento. */
export function collectFlowReferences({ slots = [], flowDoc = null, parameterForm = null } = {}) {
  const refs = {};
  const slotById = new Map((slots || []).map((s) => [Number(s.id), s]));
  const ctx = { slotById, parameterForm };

  if (flowDoc?.slots) {
    walkFlowNodes(flowDoc.slots, ctx, refs, visitNodeForRefs);
  } else if (slots?.length) {
    for (const slot of slots) {
      const node = {
        ...slot,
        acoes: slot.acoes,
        acao_parametros: slot.acao_parametros,
        resposta_modo: slot.resposta_modo,
        agente_ia: slot.agente_ia,
      };
      visitNodeForRefs(node, ctx, refs);
    }
  }

  return refs;
}

/** Snapshot de regra exportada utilizável para "criar cópia" no import. */
export function hasCreatableAutomationRuleExport(exportMeta = {}) {
  return Boolean(exportMeta?.rule || exportMeta?.snapshot);
}

/** Lista plana de itens para o wizard de importação. */
export function flattenImportItems(refs = {}, exportedRefs = {}) {
  const items = [];

  const pushKind = (kind, options = {}) => {
    const local = refs[kind] || {};
    const exported = exportedRefs[kind] || {};
    const keys = new Set([...Object.keys(local), ...Object.keys(exported)]);
    for (const key of keys) {
      const localMeta = local[key] || {};
      const exportMeta = exported[key] || {};
      const canCreateAutomation =
        kind === REF_KIND_AUTOMATION && hasCreatableAutomationRuleExport(exportMeta);
      items.push({
        key: `${kind}:${key}`,
        kind,
        sourceId: localMeta.id ?? exportMeta.id ?? key,
        label:
          exportMeta.label ||
          exportMeta.descricao ||
          exportMeta.nome ||
          localMeta.label ||
          `#${key}`,
        exportSnapshot: exportMeta.rule || exportMeta.snapshot || exportMeta,
        required: kind === REF_KIND_USER || (kind === REF_KIND_AUTOMATION && !canCreateAutomation),
        allowCreate: canCreateAutomation,
        ...options,
      });
    }
  };

  pushKind(REF_KIND_AUTOMATION);
  pushKind(REF_KIND_USER, { required: true, allowCreate: false });
  pushKind(REF_KIND_AGENT);
  pushKind(REF_KIND_CHANNEL);
  pushKind(REF_KIND_TYPE);
  pushKind(REF_KIND_SUBTYPE);
  pushKind(REF_KIND_PAYMENT);
  pushKind(REF_KIND_DELIVERY);
  pushKind(REF_KIND_POSITION);

  return items.sort((a, b) => {
    const order = {
      [REF_KIND_AUTOMATION]: 0,
      [REF_KIND_USER]: 1,
      [REF_KIND_AGENT]: 2,
      [REF_KIND_CHANNEL]: 3,
      [REF_KIND_TYPE]: 4,
      [REF_KIND_SUBTYPE]: 5,
      [REF_KIND_PAYMENT]: 6,
      [REF_KIND_DELIVERY]: 7,
      [REF_KIND_POSITION]: 8,
    };
    return (order[a.kind] ?? 99) - (order[b.kind] ?? 99) || String(a.label).localeCompare(String(b.label));
  });
}

function remapValues(values, labels, parameterForm, idMap) {
  if (!values || typeof values !== "object") return { values, labels };
  const nextValues = { ...values };
  const nextLabels = { ...(labels || {}) };

  for (const [fieldName, fieldDef] of formFieldEntries(parameterForm)) {
    const objectType = fieldDef?.object;
    const kind = ENTITY_OBJECT_MAP[objectType];
    if (!kind || !(fieldName in nextValues)) continue;

    const mapFn = (id) => {
      const mapped = idMap[`${kind}:${id}`];
      return mapped != null ? mapped : id;
    };

    const raw = nextValues[fieldName];
    if (Array.isArray(raw)) {
      nextValues[fieldName] = raw.map((v) => mapFn(v));
    } else if (raw != null && raw !== "") {
      nextValues[fieldName] = mapFn(raw);
    }
  }

  return { values: nextValues, labels: nextLabels };
}

function remapAction(action, idMap, parameterForm) {
  const tipo = Number(action?.tipo_acao) || 0;
  const idAcao = Number(action?.id_acao) || 0;
  const next = { ...action };

  if (tipo === 3 && idAcao) {
    const mapped = idMap[`${REF_KIND_AUTOMATION}:${idAcao}`];
    if (mapped != null) next.id_acao = Number(mapped);
    return next;
  }

  if (tipo === 4 && idAcao === 4 && action.values) {
    const remapped = remapValues(action.values, action.labels, parameterForm, idMap);
    next.values = remapped.values;
    next.labels = remapped.labels;
    return next;
  }

  if (tipo === 2) {
    return next;
  }

  return next;
}

function remapNode(node, idMap, parameterForm) {
  const next = { ...node };

  if (Array.isArray(next.acoes)) {
    next.acoes = next.acoes.map((a) => remapAction(a, idMap, parameterForm));
  }

  const legacyTipo = Number(next.tipo_acao) || 0;
  const legacyId = Number(next.id_acao) || 0;
  if (legacyTipo === 3 && legacyId) {
    const mapped = idMap[`${REF_KIND_AUTOMATION}:${legacyId}`];
    if (mapped != null) next.id_acao = Number(mapped);
  }

  if (next.resposta_modo === "agente_ia" && next.agente_ia?.agent_id) {
    const mapped = idMap[`${REF_KIND_AGENT}:${next.agente_ia.agent_id}`];
    if (mapped != null) {
      next.agente_ia = { ...next.agente_ia, agent_id: String(mapped) };
    }
  }

  if (next.exception) next.exception = remapNode(next.exception, idMap, parameterForm);
  if (Array.isArray(next.children)) {
    next.children = next.children.map((c) => remapNode(c, idMap, parameterForm));
  }

  return next;
}

/** Aplica mapeamentos escolhidos no wizard ao documento de fluxo. */
export function applyReferenceMappings(flowDoc, idMap = {}, { parameterForm = null } = {}) {
  if (!flowDoc?.slots) return flowDoc;
  return {
    ...flowDoc,
    slots: flowDoc.slots.map((root) => remapNode(root, idMap, parameterForm)),
  };
}

/** ID canônico da ação "Alterar a Solicitação" no catálogo de automação. */
export const AUTOMATION_ALTERAR_SOLICITACAO_ACTION_ID = 4;

/**
 * Normaliza `acoes` de regra para o formato canônico `[{ id, values }]`.
 * Regras legadas (XPro2/PHP) gravam um objeto plano de campos (ex.: transferirusuario)
 * em vez do array; sem isso o import quebra com `(d.acoes || []).map is not a function`.
 */
export function normalizeAutomationRuleAcoes(acoes, { alterarSolicitacaoId = AUTOMATION_ALTERAR_SOLICITACAO_ACTION_ID } = {}) {
  if (Array.isArray(acoes)) {
    return acoes
      .filter((a) => a != null && typeof a === "object")
      .map((a) => ({
        id: Number(a.id),
        values: a.values && typeof a.values === "object" && !Array.isArray(a.values) ? a.values : {},
      }))
      .filter((a) => Number.isFinite(a.id) && a.id > 0);
  }
  if (acoes && typeof acoes === "object") {
    return [{ id: Number(alterarSolicitacaoId) || AUTOMATION_ALTERAR_SOLICITACAO_ACTION_ID, values: { ...acoes } }];
  }
  return [];
}

export function parseAutomationRuleConfig(ruleRow) {
  if (!ruleRow) return null;
  let config = ruleRow.config;
  if (typeof config === "string") {
    try {
      config = JSON.parse(config);
    } catch {
      config = {};
    }
  }
  return {
    descricao: ruleRow.descricao,
    id_objeto: ruleRow.id_objeto,
    id_condicao: ruleRow.id_condicao,
    ids_projetos: ruleRow.ids_projetos,
    fila: ruleRow.fila,
    ordem: ruleRow.ordem,
    break: ruleRow.break,
    webhook: ruleRow.webhook,
    token: ruleRow.token,
    condicoes: config?.condicoes || {},
    acoes: normalizeAutomationRuleAcoes(config?.acoes),
    prioridade: config?.prioridade ?? null,
  };
}

function resolveExportRuleBase(exportRule) {
  if (!exportRule) return null;
  if (exportRule.rule) return exportRule.rule;
  if (exportRule.snapshot) return exportRule.snapshot;
  // Ref do export com fetch falho: { id, label, rule: null }
  if (Object.prototype.hasOwnProperty.call(exportRule, "rule") && exportRule.rule == null) {
    return null;
  }
  return exportRule;
}

export function buildAutomationRuleCreatePayload(exportRule, { chatbotEventId, descricaoSuffix = " (importado)" } = {}) {
  const base = resolveExportRuleBase(exportRule);
  if (!base) return null;
  return {
    descricao: `${String(base.descricao || "Regra").trim()}${descricaoSuffix}`,
    id_objeto: Number(base.id_objeto) || 1,
    id_condicao: Number(chatbotEventId) || Number(base.id_condicao),
    ids_projetos: base.ids_projetos ?? null,
    fila: base.fila ?? null,
    ordem: base.ordem ?? null,
    break: base.break ?? 0,
    webhook: base.webhook ?? null,
    token: base.token ?? null,
    condicoes: base.condicoes || {},
    acoes: normalizeAutomationRuleAcoes(base.acoes),
    prioridade: base.prioridade ?? null,
  };
}

export function findChatbotAutomationEventId(catalog) {
  const condicoes = catalog?.condicoes || catalog?.data?.condicoes || [];
  const byName = condicoes.find((c) =>
    String(c.nome || "")
      .toLowerCase()
      .includes("chatbot")
  );
  if (byName?.id) return Number(byName.id);
  return condicoes.length ? Number(condicoes[condicoes.length - 1].id) : 36;
}

export function groupImportItemsByStep(items = []) {
  const groups = [
    { id: "automation", kinds: [REF_KIND_AUTOMATION], titleKey: "chatbotConfigPage.flowImportWizardStepAutomation" },
    { id: "users", kinds: [REF_KIND_USER], titleKey: "chatbotConfigPage.flowImportWizardStepUsers" },
    {
      id: "entities",
      kinds: [REF_KIND_AGENT, REF_KIND_CHANNEL, REF_KIND_TYPE, REF_KIND_SUBTYPE, REF_KIND_PAYMENT, REF_KIND_DELIVERY, REF_KIND_POSITION],
      titleKey: "chatbotConfigPage.flowImportWizardStepEntities",
    },
  ];
  return groups
    .map((g) => ({
      ...g,
      items: items.filter((it) => g.kinds.includes(it.kind)),
    }))
    .filter((g) => g.items.length > 0);
}

export function validateImportMappings(items = [], idMap = {}) {
  const missing = [];
  for (const item of items) {
    if (!item.required) continue;
    const mapped = idMap[item.key];
    if (mapped == null || mapped === "") missing.push(item);
  }
  return missing;
}
